import type { IFeedsModule } from '../types';

const _guards = new WeakMap<HTMLElement, (e: KeyboardEvent) => void>();

function isInside(root: HTMLElement | null): boolean {
  const active = document.activeElement as HTMLElement | null;
  return !!root && !!active && root.contains(active);
}

function getFocusable(container: ParentNode | HTMLElement): HTMLElement[] {
  return Array.from(
    (container as ParentNode).querySelectorAll<HTMLElement>(
      'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    )
  ).filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1 && isVisible(el));
}

function isVisible(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function defaultItemSelector(): string {
  return '[role="article"]';
}

function resolveItemSelector(root: HTMLElement | null): string {
  if (!root) return defaultItemSelector();
  const sel = root.getAttribute('data-feed-item-selector');
  return sel && sel.trim().length > 0 ? sel : defaultItemSelector();
}

// Previous focusable in document order before the feed root
function getPrevInDocumentBeforeFeed(feedRoot: HTMLElement): HTMLElement | null {
  const all = getFocusable(document.body);
  const insideIndexes = all.map((el, i) => (feedRoot.contains(el) ? i : -1)).filter(i => i >= 0);
  if (insideIndexes.length === 0) return null;
  const firstInsideIdx = Math.min(...insideIndexes);
  const prevIdx = firstInsideIdx - 1;
  return prevIdx >= 0 ? all[prevIdx] : null;
}

// Next focusable after the feed root in document order
function getNextOutsideAfterFeed(feedRoot: HTMLElement): HTMLElement | null {
  const all = getFocusable(document.body);
  const insideIndexes = all.map((el, i) => (feedRoot.contains(el) ? i : -1)).filter(i => i >= 0);
  if (insideIndexes.length === 0) return null;
  const lastInsideIdx = Math.max(...insideIndexes);
  const nextIdx = lastInsideIdx + 1;
  return nextIdx < all.length ? all[nextIdx] : null;
}

const FeedsModule: IFeedsModule = {
  scrollIntoView(element: HTMLElement | null): void {
    if (!element) return;
    try {
      element.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    } catch {
      element.scrollIntoView();
    }
  },

  focusById(id: string): void {
    const el = document.getElementById(id);
    if (el) (el as HTMLElement).focus({ preventScroll: true });
  },

  scrollById(id: string): void {
    const el = document.getElementById(id);
    if (el) {
      try {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      } catch {
        el.scrollIntoView();
      }
    }
  },

  // Focus container (article element) by ID, not inner controls
  focusArticleContainerById(id: string): boolean {
    const el = document.getElementById(id);
    if (!el) return false;
    (el as HTMLElement).focus({ preventScroll: true });
    // Also scroll the article container into view to match selection feedback
    try {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    } catch {
      el.scrollIntoView();
    }
    return true;
  },

  // Retained: enter first inner focusable within an article, used elsewhere if desired
  focusFirstInArticle(articleId: string): boolean {
    const article = document.getElementById(articleId);
    if (!article) return false;
    const focusables = getFocusable(article);
    if (focusables.length > 0) {
      focusables[0].focus({ preventScroll: true });
      return true;
    }
    (article as HTMLElement).focus({ preventScroll: true });
    return true;
  },

  focusFirstOutsideAfter(feedRoot: HTMLElement): boolean {
    const next = getNextOutsideAfterFeed(feedRoot);
    if (next) {
      next.focus({ preventScroll: true });
      // Ensure the focused element is scrolled into view for consistency
      try {
        next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      } catch {
        next.scrollIntoView();
      }
      return true;
    }
    return false;
  },

  focusLastOutsideBefore(feedRoot: HTMLElement): boolean {
    const prev = getPrevInDocumentBeforeFeed(feedRoot);
    if (prev) {
      prev.focus({ preventScroll: true });
      return true;
    }
    return false;
  },

  registerFeedKeyGuards(root: HTMLElement | null): void {
    if (!root || _guards.has(root)) return;

    const itemSelector = resolveItemSelector(root);

    const getArticle = (el: HTMLElement | null): HTMLElement | null => {
      return el ? (el.closest(itemSelector) as HTMLElement | null) : null;
    };

    const getPrevArticle = (article: HTMLElement | null): HTMLElement | null => {
      let n = article?.previousElementSibling as HTMLElement | null;
      while (n) {
        if (n.matches(itemSelector)) return n;
        n = n.previousElementSibling as HTMLElement | null;
      }
      return null;
    };

    const getNextArticle = (article: HTMLElement | null): HTMLElement | null => {
      let n = article?.nextElementSibling as HTMLElement | null;
      while (n) {
        if (n.matches(itemSelector)) return n;
        n = n.nextElementSibling as HTMLElement | null;
      }
      return null;
    };

    const handler = (e: KeyboardEvent) => {
      if (!isInside(root)) return;

      const active = document.activeElement as HTMLElement | null;
      const key = e.key;

      // Prevent native scroll only for PageUp/PageDown and Ctrl+Home/End
      const isPage = key === 'PageDown' || key === 'PageUp';
      const isCtrlBoundary = e.ctrlKey && (key === 'Home' || key === 'End');
      if (isPage || isCtrlBoundary) {
        e.preventDefault();
        return;
      }

      if (key === 'Tab') {
        // Shift+Tab from feed root -> go before feed
        if (active === root && e.shiftKey) {
          const prevOutside = getPrevInDocumentBeforeFeed(root);
          if (prevOutside) {
            e.preventDefault();
            e.stopPropagation();
            prevOutside.focus({ preventScroll: true });
          }
          return;
        }

        const article = getArticle(active);
        if (!article) return;

        const focusables = getFocusable(article);
        const onArticleContainer = active === article;
        const onFirstFocusable = focusables.length > 0 && active === focusables[0];
        const onLastFocusable = focusables.length > 0 && active === focusables[focusables.length - 1];
        const hasNoInnerFocusables = focusables.length === 0;

        if (e.shiftKey) {
          // Shift+Tab revisions per APG:
          // - If on first inner focusable: focus article container
          // - If on article container: focus last inner focusable of previous article, or previous article container, or outside before feed
          // - Else: let native reverse tab order work
          if (onFirstFocusable) {
            e.preventDefault();
            e.stopPropagation();
            article.focus({ preventScroll: true });
            return;
          }

          if (onArticleContainer) {
            const prevArticle = getPrevArticle(article);
            if (prevArticle) {
              const prevFocusables = getFocusable(prevArticle);
              e.preventDefault();
              e.stopPropagation();
              if (prevFocusables.length > 0) {
                prevFocusables[prevFocusables.length - 1].focus({ preventScroll: true });
              } else {
                prevArticle.focus({ preventScroll: true });
              }
            } else {
              const prevOutside = getPrevInDocumentBeforeFeed(root);
              if (prevOutside) {
                e.preventDefault();
                e.stopPropagation();
                prevOutside.focus({ preventScroll: true });
              }
            }
            return;
          }

          // Otherwise, let native Shift+Tab go to the previous control inside the article
          return;
        } else {
          // Tab forward logic unchanged:
          if (onArticleContainer && focusables.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            focusables[0].focus({ preventScroll: true });
            return;
          }

          if (onLastFocusable || hasNoInnerFocusables) {
            const nextArticle = getNextArticle(article);
            if (nextArticle) {
              e.preventDefault();
              e.stopPropagation();
              nextArticle.focus({ preventScroll: true });
            }
            return;
          }

          // Otherwise, let native Tab move to next inner focusable
          return;
        }
      }
    };

    document.addEventListener('keydown', handler, { capture: true });
    _guards.set(root, handler);
  },

  unregisterFeedKeyGuards(root: HTMLElement | null): void {
    if (!root) return;
    const handler = _guards.get(root);
    if (handler) {
      document.removeEventListener('keydown', handler, { capture: true } as AddEventListenerOptions);
      _guards.delete(root);
    }
  }
};

export default FeedsModule;